<template>
  <Card class="p-4 flex flex-col">
    <div class="flex relative max-w-full">
            <div class="absolute sm:static flex h-20 flex-col justify-around text-xs text-primary/30 pr-2 lg:pr-4 z-0">
              <div>{{ $t('upcomingRain.maxIntensity') }}</div>
              <div>{{ $t('upcomingRain.midIntensity') }}</div>
              <div>{{ $t('upcomingRain.minIntensity') }}</div>
            </div>
      <div class="flex-grow flex flex-col">
        <div class="flex-grow flex items-end h-20 relative">
          <!-- Horizontal dashed lines for thirds -->
          <div class="absolute bottom-1/3 left-0 right-0 border-t border-dashed border-primary/20"></div>
          <div class="absolute bottom-2/3 left-0 right-0 border-t border-dashed border-primary/20"></div>
          <div class="absolute top-0 left-0 right-0 border-t border-dashed border-primary/20"></div>
          <div class="absolute bottom-0 left-0 right-0 border-t border-primary/20"></div>
          <div v-for="point in chart.data" :key="point.time"
               :style="{ height: calculateBarHeight(point.intensity) + '%', left: point.left, width: chart.barWidth }"
               class="absolute bg-blue-500 rounded-t-full flex-shrink-0 -translate-x-1/2">
          </div>
        </div>
        <!-- MinutelyTimeline.vue content starts here -->
        <div class="relative h-4 pt-1 mt-1 text-xs text-primary/40">
          <span
              class="absolute text-nowrap font-semibold"
          >
            <div class="absolute -top-2 w-px h-2 bg-primary/40 left-0"></div>
            {{ $t('minutelyTimeline.now') }}
          </span>
          <span
              v-for="tick in ticks"
              :key="tick.minute"
              class="absolute text-nowrap -translate-x-1/2"
              :style="tick.style"
          >
            <div class="absolute -top-2 w-px h-2 bg-primary/40 left-1/2"></div>
            {{ tick.label }}
          </span>
        </div>
        <!-- MinutelyTimeline.vue content ends here -->
      </div>
    </div>
  </Card>
</template>

<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Card from "./Card.vue";

const props = defineProps({
  minutely: Object,
});

const { t } = useI18n();

const maxIntensity = 8;
const exponent = 2;

const calculateBarHeight = (intensity) => {
  const normalizedIntensity = Math.min(1, Math.max(0, intensity / maxIntensity));
  const mappedIntensity = 1 - Math.pow(1 - normalizedIntensity, exponent);
  return mappedIntensity * 100;
};

// Blend nearby values for display, preserving the provider's dry intervals.
// Time-based weights give the same smoothing width for every provider cadence.
const smoothIntensity = (point, data) => {
  if (point.intensity <= 0) return 0;
  const radiusSeconds = 7 * 60;
  let weightedIntensity = 0;
  let totalWeight = 0;
  for (const neighbour of data) {
    const weight = Math.max(0, 1 - Math.abs(neighbour.time - point.time) / radiusSeconds);
    weightedIntensity += Math.max(0, neighbour.intensity) * weight;
    totalWeight += weight;
  }
  return weightedIntensity / totalWeight;
};

// Use elapsed time so providers with different sampling intervals share the same axis.
const chart = computed(() => {
  const data = props.minutely?.data ?? [];
  if (!data.length) return { data: [], duration: 0, barWidth: '0%' };
  const start = data[0].time;
  const steps = data.slice(1).map((point, index) => point.time - data[index].time).filter(step => step > 0);
  const step = steps.length ? Math.min(...steps) : 60;
  const visible = data.filter(point => point.time >= start && point.time < start + 7200);
  const duration = Math.min(7200, visible[visible.length - 1].time - start + step);
  return {
    data: visible.map(point => ({
      ...point,
      intensity: smoothIntensity(point, data),
      left: `${(point.time - start + Math.min(step, start + duration - point.time) / 2) / duration * 100}%`,
    })),
    duration,
    barWidth: `${step / duration * 80}%`,
  };
});

const ticks = computed(() => {
  const durationMinutes = chart.value.duration / 60;
  const interval = durationMinutes > 60 ? 20 : 10;
  const minutes = [];
  for (let minute = interval; minute < durationMinutes; minute += interval) minutes.push(minute);
  if (durationMinutes > 0) minutes.push(durationMinutes);
  return minutes.map(minute => ({
    minute,
    label: minute === durationMinutes
      ? t('minutelyTimeline.minutesWithUnit', { minutes: minute })
      : minute.toString(),
    style: {
      left: `${minute / durationMinutes * 100}%`,
      transform: minute === durationMinutes ? 'translateX(-100%)' : 'translateX(-50%)',
    },
  }));
});
</script>
